import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Box, Image, Text } from '@chakra-ui/react'
import MenuCategories from '@/components/menus/menu-categories'
import Carousel from '@/components/carousels/carousel'
import ProductSlider from '@/components/sliders/products-slider'
import Layout from '@/components/layout/layout'
import Certifications from '@/components/layout/parts/certifications'
import imageAssets1 from '/img/banner-web.png'
import imageAssets2 from '/img/pexels-victorfreitas-841131.jpg'
import imageAssets3 from '/img/supplements.webp'
import imageAssets4 from '/img/pexels-goumbik-669577.jpg'
import { Dumbbell, Headphones, Shirt, Target, Zap } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import {
	filterByCategoryAndType,
	filterByPromotions,
} from '@/services/api/products/products-filter'
import Toast from '@/components/elements/toast'
import { Entities } from '@/entities/api/entities'
import { Link, useNavigate } from 'react-router-dom'
import { entityToSpanish } from '@/utils/entity-convert'
import {
	setAccessoriesProducts,
	setClothesProducts,
	setPlansProducts,
	setPromoProducts,
	setSupplementsProducts,
} from '@/store/features/products'
import { FilteredProductsResponse } from '@/entities/api/products/product-filters'
import CustomersEntity from '@/entities/api/customers/customer'

const Home = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const categories = useSelector(
		(state: RootState) => state.category.categories
	)
	const {
		promoProducts,
		supplementsProducts,
		clothesProducts,
		plansProducts,
		accessoriesProducts,
	} = useSelector((state: RootState) => state.product)

	const [loadingStates, setLoadingStates] = useState({
		promo: false,
		supplements: false,
		clothes: false,
		accessories: false,
		plans: false,
	})

	const loadedCategories = useRef({
		promo: false,
		supplements: false,
		clothes: false,
		accessories: false,
		plans: false,
	})

	const shopTypes = useMemo(
		() => [
			{
				icon: Dumbbell,
				name: 'Suplementos',
				color: 'from-red-600 to-red-700',
				type: Entities.SUPPLEMENTS,
			},
			// {
			// 	icon: Shirt,
			// 	name: 'Ropa Deportiva',
			// 	color: 'from-red-600 to-red-700',
			// 	type: Entities.CLOTHES,
			// },
			// {
			// 	icon: Headphones,
			// 	name: 'Accesorios',
			// 	color: 'from-red-600 to-red-700',
			// 	type: Entities.ACCESSORIES,
			// },
			{
				icon: Target,
				name: 'Planes Personalizados',
				color: 'from-red-600 to-red-700',
				type: Entities.PLANS_PERSONALIZED,
			},
		],
		[]
	)

	const navigate = useNavigate()
	const dispatch = useDispatch<AppDispatch>()

	const updateLoadingState = useCallback(
		(key: keyof typeof loadingStates, value: boolean) => {
			setLoadingStates((prev) => ({ ...prev, [key]: value }))
		},
		[]
	)

	const getPromoProducts = useCallback(async () => {
		if (loadedCategories.current.promo || loadingStates.promo) return

		updateLoadingState('promo', true)
		loadedCategories.current.promo = true

		try {
			const response = await filterByPromotions({
				onlyWithDiscount: true,
				page: 1,
				limit: 24,
			})

			if ([400, 401].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar los productos en promoción',
				})
			} else {
				dispatch(setPromoProducts(response?.data ?? { products: [], total: 0 }))
			}
		} catch (error) {
			Toast({
				type: 'error',
				message: 'No se pudieron cargar los productos en promoción',
			})
		} finally {
			updateLoadingState('promo', false)
		}
	}, [loadingStates.promo, dispatch, updateLoadingState])

	const getSupplementsProducts = useCallback(async () => {
		if (loadedCategories.current.supplements || loadingStates.supplements)
			return

		updateLoadingState('supplements', true)
		loadedCategories.current.supplements = true

		try {
			const response = await filterByCategoryAndType({
				type: Entities.SUPPLEMENTS,
				page: 1,
				limit: 24,
			})

			if ([400, 401].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar los suplementos',
				})
			} else {
				dispatch(
					setSupplementsProducts(response?.data ?? { products: [], total: 0 })
				)
			}
		} catch (error) {
			Toast({
				type: 'error',
				message: 'No se pudieron cargar los suplementos',
			})
		} finally {
			updateLoadingState('supplements', false)
		}
	}, [loadingStates.supplements, dispatch, updateLoadingState])

	// const getClothesProducts = useCallback(async () => {
	// 	if (loadedCategories.current.clothes || loadingStates.clothes) return

	// 	updateLoadingState('clothes', true)
	// 	loadedCategories.current.clothes = true

	// 	try {
	// 		const response = await filterByCategoryAndType({
	// 			type: Entities.CLOTHES,
	// 			page: 1,
	// 			limit: 24,
	// 		})

	// 		if ([400, 401].includes(response?.status ?? 0)) {
	// 			Toast({
	// 				type: 'error',
	// 				message: 'No se pudieron cargar la ropa',
	// 			})
	// 		} else {
	// 			dispatch(
	// 				setClothesProducts(response?.data ?? { products: [], total: 0 })
	// 			)
	// 		}
	// 	} catch (error) {
	// 		Toast({
	// 			type: 'error',
	// 			message: 'No se pudieron cargar la ropa',
	// 		})
	// 	} finally {
	// 		updateLoadingState('clothes', false)
	// 	}
	// }, [loadingStates.clothes, dispatch, updateLoadingState])

	// const getAccessoriesProducts = useCallback(async () => {
	// 	if (loadedCategories.current.accessories || loadingStates.accessories)
	// 		return

	// 	updateLoadingState('accessories', true)
	// 	loadedCategories.current.accessories = true

	// 	try {
	// 		const response = await filterByCategoryAndType({
	// 			type: Entities.ACCESSORIES,
	// 			page: 1,
	// 			limit: 24,
	// 		})

	// 		if ([400, 401].includes(response?.status ?? 0)) {
	// 			Toast({
	// 				type: 'error',
	// 				message: 'No se pudieron cargar los accesorios',
	// 			})
	// 		} else {
	// 			dispatch(
	// 				setAccessoriesProducts(response?.data ?? { products: [], total: 0 })
	// 			)
	// 		}
	// 	} catch (error) {
	// 		Toast({
	// 			type: 'error',
	// 			message: 'No se pudieron cargar los accesorios',
	// 		})
	// 	} finally {
	// 		updateLoadingState('accessories', false)
	// 	}
	// }, [loadingStates.accessories, dispatch, updateLoadingState])

	const getPlansProducts = useCallback(async () => {
		if (loadedCategories.current.plans || loadingStates.plans) return

		updateLoadingState('plans', true)
		loadedCategories.current.plans = true

		try {
			const response = await filterByCategoryAndType({
				type: Entities.PLANS_PERSONALIZED,
				page: 1,
				limit: 24,
			})

			if ([400, 401].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar los planes',
				})
			} else {
				dispatch(setPlansProducts(response?.data ?? { products: [], total: 0 }))
			}
		} catch (error) {
			Toast({
				type: 'error',
				message: 'No se pudieron cargar los planes',
			})
		} finally {
			updateLoadingState('plans', false)
		}
	}, [loadingStates.plans, dispatch, updateLoadingState])

	useEffect(() => {
		const loadAllProducts = async () => {
			await Promise.allSettled([
				getSupplementsProducts(),
				// getClothesProducts(),
				// getAccessoriesProducts(),
				getPlansProducts(),
				getPromoProducts(),
			])
		}

		loadAllProducts()
	}, [
		getSupplementsProducts,
		// getClothesProducts,
		// getAccessoriesProducts,
		getPlansProducts,
		getPromoProducts,
	])

	const goCategory = useCallback(
		(type: Entities) => {
			navigate(`/Categoria/${entityToSpanish(type)}`)
		},
		[navigate]
	)

	const goPromotions = useCallback(() => {
		navigate('/Promociones')
	}, [navigate])

	const goSupplements = useCallback(
		() => goCategory(Entities.SUPPLEMENTS),
		[goCategory]
	)
	const goClothes = useCallback(
		() => goCategory(Entities.CLOTHES),
		[goCategory]
	)
	const goAccessories = useCallback(
		() => goCategory(Entities.ACCESSORIES),
		[goCategory]
	)
	const goPlans = useCallback(
		() => goCategory(Entities.PLANS_PERSONALIZED),
		[goCategory]
	)

	return (
		<Layout customer={customer} token={token}>
			<div className='w-full flex flex-col relative'>
				<div className='flex justify-center items-start'>
					<div className='w-full max-w-none px-0 md:px-2 lg:px-4 xl:px-8'>
						<div className='w-full flex relative'>
							<MenuCategories categories={categories} />
							<Carousel />
						</div>
					</div>
				</div>
			</div>
			{promoProducts?.products && promoProducts?.products?.length > 0 && (
				<Box padding={{ base: '4vh 2vw', lg: '4vh 6vw' }} w='full'>
					<ProductSlider
						title='Descuentos que Motivan'
						category='Promociones'
						filteredProducts={promoProducts as FilteredProductsResponse}
						useExternalPagination={false}
						goZone={goPromotions}
						customerId={customer?._id || ''}
						customer={customer}
					/>
				</Box>
			)}

			<div className='w-full flex flex-col px-[2vw] lg:px-[6vw] py-[4vh] items-center bg-gradient-to-b from-white to-gray-50'>
				<div className='w-full mb-8'>
					<div className='bg-white rounded-xl border-l-8 border-red-600 p-6 shadow-sm hover:shadow-md transition-shadow duration-300'>
						<div className='flex items-center space-x-4'>
							<div className='w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center'>
								<Target className='w-5 h-5 text-white' />
							</div>
							<div>
								<h2 className='text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 leading-tight'>
									Nuestras{' '}
									<span className='bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent'>
										Categorías
									</span>
								</h2>
								<p className='text-gray-600 text-sm mt-1'>
									Encuentra todo lo que necesitas para tu entrenamiento
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className='w-full max-w-7xl flex flex-wrap justify-center items-center gap-5 sm:gap-6 md:gap-8'>
					{shopTypes.map((category, index) => (
						<Link
							to={`/Categoria/${entityToSpanish(category.type)}`}
							key={index}
							className='group relative w-full sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)] lg:w-[calc(25%-24px)] h-44 md:h-48 lg:h-52 border-2 border-gray-200 rounded-2xl p-6 bg-white hover:shadow-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 ease-in-out transform hover:-translate-y-1 overflow-hidden'
						>
							<div className='absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300'>
								<div
									className='absolute inset-0'
									style={{
										backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Ccircle cx='10' cy='10' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
									}}
								/>
							</div>

							<div className='relative flex flex-col items-center justify-center h-full gap-4'>
								<div
									className={`w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] lg:w-20 lg:h-20 bg-gradient-to-br ${category.color} rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300 relative overflow-hidden`}
								>
									<div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700' />

									<category.icon className='w-8 h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 text-white group-hover:scale-110 transition-transform duration-300' />
								</div>

								<h3 className='font-bold text-gray-900 text-lg md:text-xl text-center leading-tight group-hover:text-red-600 transition-colors duration-300 px-2'>
									{category.name}
								</h3>

								<div className='absolute bottom-4 left-1/2 -translate-x-1/2 w-8 h-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
							</div>
						</Link>
					))}
				</div>
				<div className='w-full max-w-7xl mt-12 flex justify-center'>
					<div className='flex items-center space-x-2 text-gray-400'>
						<div className='w-12 h-px bg-gradient-to-r from-transparent to-gray-300'></div>
						<Zap className='w-4 h-4 text-red-600' />
						<div className='w-12 h-px bg-gradient-to-l from-transparent to-gray-300'></div>
					</div>
				</div>
			</div>
			{supplementsProducts?.products &&
				supplementsProducts?.products.length > 0 && (
					<Box padding={{ base: '4vh 2vw', lg: '4vh 6vw' }} w='full'>
						<ProductSlider
							title='Potencia tu cuerpo'
							category='suplementos'
							filteredProducts={supplementsProducts as FilteredProductsResponse}
							useExternalPagination={false}
							goZone={goSupplements}
							customerId={customer?._id || ''}
							customer={customer}
						/>
					</Box>
				)}
			<div className='w-full relative mt-4 mb-4 px-[1vw] py-[0.5vh] lg:px-[6vw] lg:py-[2vh]'>
				<div className='w-full h-[60vh] md:h-[70vh] lg:h-[90vh] rounded-md overflow-hidden shadow-md'>
					<img
						src={imageAssets1}
						alt='Placeholder Image'
						className='w-full h-full object-cover object-top rounded-md'
					/>
				</div>
			</div>

			{/* {clothesProducts?.products && clothesProducts.products.length > 0 && (
			
				<Box padding={{ base: '4vh 2vw', lg: '4vh 6vw' }} w='full'>
					<ProductSlider
						title='Vístete para rendir'
						category='Ropa'
						filteredProducts={clothesProducts as FilteredProductsResponse}
						useExternalPagination={false}
						goZone={goClothes}
						customerId={customer?._id || ''}
						customer={customer}
					/>
				</Box>
			)}  */}
			<Box
				w='full'
				h={{ base: 'auto', lg: '80vh' }}
				position='relative'
				mt='4'
				mb='4'
				padding={{ base: '0 1vw', lg: '2vh 6vw' }}
				display='flex'
				flexDirection={{ base: 'column', lg: 'row' }}
			>
				<Box
					w={{ base: '100%', lg: '50%' }}
					h={{ base: '70vh', lg: '100%' }}
					mr='4'
					position='relative'
					borderRadius='lg'
					overflow='hidden'
					boxShadow='lg'
					display='flex'
					justifyContent='center'
				>
					<Image
						src={imageAssets2}
						alt='Placeholder Image Left'
						objectFit='cover'
						w='100%'
						h='100%'
					/>
					<Box
						position='absolute'
						bottom='10px'
						color='white'
						textAlign='center'
						p='4'
						bg='rgba(0, 0, 0, 0.5)'
						borderRadius='lg'
					>
						<Text
							fontSize={{ base: 'sm', sm: 'sm', md: 'md', lg: 'lg' }}
							fontWeight='bold'
						>
							Planes de entrenamiento y alimentación hechos para ti.
						</Text>
					</Box>
					<Box
						position='absolute'
						top='0'
						left='2vw'
						bg='red'
						w={{ base: '8vw', lg: '4vw' }}
						h='12vh'
						borderRadius='0 md 0 md'
						display='flex'
						justifyContent='center'
						alignItems='center'
						color='white'
						fontWeight='bold'
						fontSize='sm'
						borderBottomLeftRadius='md'
						borderBottomRightRadius='md'
					>
						Plan
					</Box>
				</Box>

				<Box
					w={{ base: '100%', lg: '50%' }}
					mt={{ base: '4', lg: '0' }}
					h={{ base: '100vh', lg: '100%' }}
					display='flex'
					flexDirection='column'
					justifyContent='space-between'
					borderRadius='lg'
					overflow={{ base: 'hidden', lg: 'hidden' }}
				>
					<Box w='100%' h='99%' position='relative' boxShadow='inner'>
						<Image
							src={imageAssets3}
							alt='Placeholder Image Top Right'
							objectFit='cover'
							w='100%'
							h='100%'
						/>
						<Box
							position='absolute'
							bottom='10px'
							left='50%'
							transform='translateX(-50%)'
							color='white'
							textAlign='center'
							p={2}
							bg='rgba(0, 0, 0, 0.6)'
							borderRadius='md'
						>
							<Text
								fontSize={{ base: 'sm', sm: 'sm', md: 'md' }}
								fontWeight='bold'
							>
								Lleva tu entrenamiento al siguiente nivel con la mejor
								suplementación.
							</Text>
						</Box>
					</Box>

					{/* <Box w='100%' h='49%' position='relative' boxShadow='inner'>
						<Image
							src={imageAssets4}
							alt='Placeholder Image Bottom Right'
							objectFit='cover'
							w='100%'
							h='100%'
						/>
						<Box
							position='absolute'
							bottom='10px'
							left='50%'
							transform='translateX(-50%)'
							color='white'
							textAlign='center'
							p={2}
							bg='rgba(0, 0, 0, 0.6)'
							borderRadius='md'
						>
							<Text
								fontSize={{ base: 'sm', sm: 'sm', md: 'md' }}
								fontWeight='bold'
							>
								Accesorios que potencian tu entrenamiento
							</Text>
						</Box>
					</Box> */}
				</Box>
			</Box>
			{plansProducts?.products && plansProducts.products.length > 0 && (
				<Box padding={{ base: '4vh 2vw', lg: '4vh 6vw' }} w='full'>
					<ProductSlider
						title='Planifica tu transformación'
						category='entrenamiento'
						customerId={customer?._id || ''}
						customer={customer}
						filteredProducts={plansProducts as FilteredProductsResponse}
						useExternalPagination={false}
						goZone={goPlans}
					/>
				</Box>
			)}
			{/* {accessoriesProducts?.products &&
				accessoriesProducts.products.length > 0 && (
					<Box padding={{ base: '4vh 2vw', lg: '4vh 6vw' }} w='full'>
						<ProductSlider
							title='Comodidad al entrenar'
							category='Accesorios'
							customerId={customer?._id || ''}
							customer={customer}
							filteredProducts={accessoriesProducts as FilteredProductsResponse}
							useExternalPagination={false}
							goZone={goAccessories}
						/>
					</Box>
				)} */}
			<Certifications />
		</Layout>
	)
}

export default Home
